// ─────────────────────────────────────────────────────────────────────────────
// firebase/config.ts – Firebase Web SDK Initialisation
//
// ── ZWEI APP-INSTANZEN, ZWEI PERSISTENZ-SLOTS (2026-09-08, T-M34.2 ─────────
//    REQ-SC-109 (1), (3), (4))
// Firebase Auth haelt je App-Instanz GENAU EINEN `currentUser` und legt ihn je
// Ursprung unter EINEM Schluessel ab (`firebase:authUser:<apiKey>:[DEFAULT]`).
// Solange Verwaltung und Bildschirme dieselbe Instanz benutzten, WAR die
// Anmeldung eines Bildschirms die Abmeldung des Verwalters — in jedem Tab
// desselben Ursprungs. Am 2026-09-08 ist das im Testsystem reproduziert
// worden (30 Umlaeufe in 40 s; `arbeitsdokumente/analyse-screen-sitzung-
// 2026-09-08.md`).
//
// Deshalb gibt es hier ZWEI Instanzen, und die Zuordnung ist abschliessend:
//   • `[DEFAULT]` — `firebaseApp` / `db` / `auth` / `functions`: DIE
//     VERWALTUNGSOBERFLAECHE. Sie bleibt Zeichen fuer Zeichen, wie sie war.
//   • `'screen'`  — `screenApp` / `screenAuth` / `screenDb`: DIE DREI
//     BILDSCHIRM-ANSICHTEN (Kitchen TV, Abholscreen, Fastline). Ihre Anmeldung
//     ist mit `initializeAuth(..., { persistence: browserSessionPersistence })`
//     gebildet und lebt damit JE TAB (`sessionStorage` statt IndexedDB): sie
//     ueberlebt das Neuladen derselben Registerkarte und wandert nicht in
//     andere. Das kostet nichts — ein Kiosk-Geraet meldet sich beim Laden
//     ohnehin ueber `screentoken` neu an (REQ-SEC-024, REQ-OPS-053 (f)).
//
// ⚠ ES ENTSTEHT KEIN ZWEITER ZUGANGSWEG. `screentoken` bleibt der einzige
//   Ausstellungsort, die Claims (`screen`, `screenPrinterId`, `companyId`)
//   bleiben unveraendert, und `firestore.rules` ist von dieser Trennung mit
//   keiner Zeile beruehrt (REQ-SC-050/051, REQ-SEC-012, REQ-SEC-024). Es
//   wechselt allein der Ort, an dem die ausgestellte Sitzung liegt.
//
// ⚠ DER EMULATOR-ZWEIG GILT FUER BEIDE INSTANZEN. Faende er nur die
//   Portal-Instanz, spraeche die Bildschirm-Instanz im Emulatorlauf gegen die
//   Produktion — ein Fehlgriff, der nirgends rot wuerde.
//
// ⚠ BENANNTE GRENZE: zwei Instanzen sind zwei Firestore-Verbindungen, wenn
//   Portal und Bildschirm im SELBEN Tab liefen. Im Betrieb ist das kein Fall
//   (die Bildschirme laufen auf eigenen Geraeten); im Entwicklungsfall ist es
//   der Preis der Trennung (REQ-SC-109, Grenze (i)).
// ─────────────────────────────────────────────────────────────────────────────

import { initializeApp }              from 'firebase/app';
import { getFirestore,
         connectFirestoreEmulator }   from 'firebase/firestore';
import { getAuth,
         initializeAuth,
         browserSessionPersistence,
         connectAuthEmulator }        from 'firebase/auth';
import { getFunctions,
         connectFunctionsEmulator }   from 'firebase/functions';

export const firebaseConfig = {
  apiKey:            import.meta.env.VITE_FIREBASE_API_KEY             as string,
  authDomain:        import.meta.env.VITE_FIREBASE_AUTH_DOMAIN         as string,
  projectId:         import.meta.env.VITE_FIREBASE_PROJECT_ID          as string,
  storageBucket:     import.meta.env.VITE_FIREBASE_STORAGE_BUCKET      as string,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID as string,
  appId:             import.meta.env.VITE_FIREBASE_APP_ID              as string,
};

export const firebaseApp = initializeApp(firebaseConfig);
export const db          = getFirestore(firebaseApp);
export const auth        = getAuth(firebaseApp);
export const functions   = getFunctions(firebaseApp, 'europe-west6');

// ── Die Instanz der BILDSCHIRM-ANSICHTEN (T-M34.2, REQ-SC-109 (1)) ───────────
//
// `initializeAuth` und NICHT `getAuth`: nur so laesst sich die Persistenz beim
// Bilden festlegen. `getAuth(screenApp)` naehme die Vorgabe (IndexedDB bzw.
// `localStorage`) und legte die Anzeige-Sitzung wieder ursprungsweit ab — die
// Trennung waere dann nur halb.
//
// Es gibt hier bewusst KEINE `functions`-Instanz: die drei Bildschirm-
// Funktionen (`screentoken`, `screenScan`, `kitchenaction`) sind `onRequest`
// und werden in `firebase/screenApi.ts` per `fetch` mit dem ID-Token aus
// `screenAuth` gerufen (REQ-SC-063). `callable()` weiter unten ist der Weg der
// VERWALTUNG und hat mit den Bildschirmen nichts zu tun.
export const screenApp  = initializeApp(firebaseConfig, 'screen');
export const screenAuth = initializeAuth(screenApp, { persistence: browserSessionPersistence });
export const screenDb   = getFirestore(screenApp);

export const isEmulator = import.meta.env.VITE_USE_EMULATORS === 'true';

// ── Local Emulator support ────────────────────────────────────────────────────
// ⚠ BEIDE Instanzen werden umgeschaltet (REQ-SC-109 (1), Emulator-Zweig).
if (isEmulator) {
  connectFirestoreEmulator(db,        'localhost', 8080);
  connectAuthEmulator(auth,           'http://localhost:9099', { disableWarnings: true });
  connectFunctionsEmulator(functions, 'localhost', 5001);
  connectFirestoreEmulator(screenDb,  'localhost', 8080);
  connectAuthEmulator(screenAuth,     'http://localhost:9099', { disableWarnings: true });
  console.info('[Firebase] Using local emulators.');
}

// ─────────────────────────────────────────────────────────────────────────────
// callable() – CORS-safe Cloud Function caller
//
// In production: POSTs directly to /api/<name> (Firebase Hosting rewrite)
//   → same origin as printerqueue.payfix.app → NO CORS
// In emulator:   Uses the Firebase SDK httpsCallable via localhost:5001
// ─────────────────────────────────────────────────────────────────────────────
export async function callable<Res = unknown>(
  name: string,
  data: unknown = null,
): Promise<Res> {
  // Emulator: use Firebase SDK (no CORS issue on localhost)
  if (isEmulator) {
    const { httpsCallable: hc } = await import('firebase/functions');
    const fn = hc<unknown, Res>(functions, name);
    const result = await fn(data);
    return result.data;
  }

  // Production: POST to /api/<name> via hosting rewrite → same origin → no CORS
  // Functions are onRequest, so we send plain JSON (not Firebase callable protocol)
  const user  = auth.currentUser;
  const token = user ? await user.getIdToken() : null;

  const response = await fetch(`/api/${name}`, {
    method:  'POST',
    headers: {
      'Content-Type':  'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
    },
    body: JSON.stringify(data ?? {}),
  });

  const json = await response.json();

  if (!response.ok) {
    const msg = json.error ?? `HTTP ${response.status}`;
    const err  = new Error(msg);
    (err as any).details = json;
    throw err;
  }

  return json as Res;
}
